/**
 * @fileoverview Institution documents component for displaying and managing institution documents
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { intl, fetchData, fetchUrl, HTTP_STATUS, DmsButton, DmsPermissions, Notifications, getToken } from "dmsbasereact";
import { getBaseURL } from "../../build_config";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArticleIcon from "@mui/icons-material/Article";
import AddIcon from "@mui/icons-material/Add";
import { DmsInfoCard } from "../../components/DmsDetailModal/DmsInfoCard";
import { DocumentUploadSlot } from "../../components/DocumentUploadSlot/DocumentUploadSlot";
import { getTranslatedDocumentName, normalizeDocumentName } from "../../utils/documentTranslations";
import { formatDate } from "../../utils/formatters";
import { Permissions } from "../../constants/Permissions";
import { DocumentStatus, DocumentStatusEnum } from "../../constants/DocumentStatus";
import { DmsFileViewerModal } from "../../components/DmsFileViewer";

/**
 * Status icons mapping
 */
const STATUS_ICONS = {
  [DocumentStatusEnum.PENDING_REVIEW]: <HourglassEmptyIcon fontSize="small" />,
  [DocumentStatusEnum.APPROVED]: <CheckCircleIcon fontSize="small" />,
  [DocumentStatusEnum.REJECTED]: <ErrorIcon fontSize="small" />,
  [DocumentStatusEnum.EXPIRED]: <ErrorIcon fontSize="small" />,
};

/**
 * Component for displaying and uploading institution documents
 */
export const InstitutionDocuments = ({ institutionPk = null,
  allowUpload = false}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const fetchDocuments = useCallback(async () => {
    if (!institutionPk) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchData(`/admin/institutions/${institutionPk}/documents`, "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        setDocuments(response.data?.data || []);
      } else {
        setError(intl.get("FailedToLoadDocuments").d("Failed to load documents"));
      }
    } catch (err) {
      setError(intl.get("FailedToLoadDocuments").d("Failed to load documents"));
    } finally {
      setLoading(false);
    }
  }, [institutionPk]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Fetch document types when upload modal opens
  useEffect(() => {
    const fetchDocTypes = async () => {
      if (uploadModalOpen && documentTypes.length === 0) {
        try {
          const response = await fetchData("/admin/institutions/document-types", "GET", true);
          if (response.status === HTTP_STATUS.OK_200) {
            setDocumentTypes(response.data?.data || []);
          }
        } catch (err) {
          // Silently fail - user will see empty dropdown
        }
      }
    };
    fetchDocTypes();
  }, [uploadModalOpen, documentTypes.length]);

  const handleDownload = async (filePk, fileName) => {
    try {
      const token = getToken();
      const response = await fetch(`${getBaseURL()}/file/${filePk}`, {
        headers: {
          'Authorization': `Bearer ${token?.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      Notifications.toast(intl.get("FailedToDownloadFile").d("Failed to download file"), Notifications.TYPE_ERROR);
    }
  };

  const handleViewDocument = (doc) => {
    if (!doc.file_pk) return;
    setSelectedDocument({
      filePk: doc.file_pk,
      fileName: doc.file_name || doc.document_type_label || intl.get("Document").d("Document"),
    });
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedDocument(null);
  };

  const handleFileSelect = (docName, file) => {
    setUploadFile(file);
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedDocType) {
      Notifications.toast(intl.get("SelectDocumentTypeAndFile").d("Please select a document type and file"), Notifications.TYPE_WARNING);
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", uploadFile);

      const response = await fetchUrl(
        `/admin/institutions/${institutionPk}/documents/upload?document_type=${selectedDocType}`,
        'POST',
        true,
        formData,
        false,
        null,
        null,
        null,
        'multipart/form-data',
        false
      );

      if (response.status === HTTP_STATUS.CREATED || response.status === HTTP_STATUS.OK || response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(intl.get("DocumentUploadedSuccessfully").d("Document uploaded successfully"), Notifications.TYPE_SUCCESS);
        setUploadModalOpen(false);
        setSelectedDocType("");
        setUploadFile(null);
        fetchDocuments();
      } else {
        Notifications.toast(response.data?.detail || intl.get("FailedToUploadDocument").d("Failed to upload document"), Notifications.TYPE_ERROR);
      }
    } catch (err) {
      Notifications.toast(intl.get("FailedToUploadDocument").d("Failed to upload document"), Notifications.TYPE_ERROR);
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    setSelectedDocType("");
    setUploadFile(null);
  };

  if (loading) {
    return (
      <DmsInfoCard icon={<ArticleIcon />} title={intl.get("Documents").d("Documents")}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </DmsInfoCard>
    );
  }

  if (error) {
    return (
      <DmsInfoCard icon={<ArticleIcon />} title={intl.get("Documents").d("Documents")}>
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      </DmsInfoCard>
    );
  }

  return (
    <>
      <DmsInfoCard icon={<ArticleIcon />} title={intl.get("Documents").d("Documents")}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {documents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {intl.get("NoDocumentsUploaded").d("No documents uploaded")}
            </Typography>
          ) : (
            <List dense>
              {documents.map((doc) => (
                <ListItem
                  key={doc.pk}
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    mb: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <ListItemIcon>
                    <DescriptionIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={getTranslatedDocumentName(normalizeDocumentName(doc.document_type_label))}
                    secondary={
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {doc.file_name && (
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                            {doc.file_name}
                          </Typography>
                        )}
                        <Chip
                          size="small"
                          label={doc.status_label}
                          color={doc.is_exempt ? "info" : DocumentStatus.getColor(doc.status)}
                          icon={STATUS_ICONS[doc.status]}
                          sx={{ width: "fit-content" }}
                        />
                        {doc.review_notes && (
                          <Typography variant="caption" color="text.secondary">
                            {intl.get("Notes").d("Notes")}: {doc.review_notes}
                          </Typography>
                        )}
                        {doc.created_on && (
                          <Typography variant="caption" color="text.secondary">
                            {intl.get("UploadedOn").d("Uploaded on")}: {new Date(doc.created_on).toLocaleDateString('pt-BR')}
                          </Typography>
                        )}
                        {doc.is_exempt ? (
                          <>
                            <Chip
                              label={intl.get("Document.ExpirationExempt").d("Expiry waived")}
                              color="info"
                              size="small"
                              variant="outlined"
                              sx={{ width: "fit-content" }}
                            />
                            {doc.exemption_note && (
                              <Box sx={{ mt: 0.5, mb: 0.5, px: 1, py: 0.5, border: "1px solid", borderColor: "info.main", borderRadius: 1, maxWidth: "80%" }}>
                                <Typography variant="caption" color="text.secondary">
                                  {doc.exemption_note}
                                </Typography>
                              </Box>
                            )}
                          </>
                        ) : doc.expiration_date ? (
                          <Typography variant="caption" color="text.secondary">
                            {intl.get("Expires").d("Expires")}: {formatDate(doc.expiration_date)}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: "div" }}
                  />
                  {doc.file_pk && (
                  <ListItemSecondaryAction>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title={intl.get("Tooltip.Action.View").d("Visualizar")}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewDocument(doc)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={intl.get("Tooltip.Action.Download").d("Baixar")}>
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(doc.file_pk, doc.file_name)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          )}

          {/* Upload Button with permission check - only shown if allowUpload is true */}
          {allowUpload && (
            <DmsPermissions permissions={Permissions.INSTITUTION_DOCUMENTS_UPLOAD}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <DmsButton
                  icon={<AddIcon />}
                  text={intl.get("UploadDocument").d("Upload Document")}
                  onClick={() => setUploadModalOpen(true)}
                  size="small"
                />
              </Box>
            </DmsPermissions>
          )}
        </Box>
      </DmsInfoCard>

      {/* Upload Document Modal */}
      <Dialog open={uploadModalOpen} onClose={handleCloseUploadModal} maxWidth="sm" fullWidth>
        <DialogTitle>{intl.get("UploadDocument").d("Upload Document")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{intl.get("DocumentType").d("Document Type")}</InputLabel>
              <Select
                value={selectedDocType}
                label={intl.get("DocumentType").d("Document Type")}
                onChange={(e) => setSelectedDocType(e.target.value)}
              >
                {documentTypes.map((dtype) => (
                  <MenuItem key={dtype.value} value={dtype.value}>
                    {dtype.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DocumentUploadSlot
              docName={intl.get("SelectFile").d("Select File")}
              file={uploadFile}
              onFileSelect={handleFileSelect}
              disabled={uploading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <DmsButton
            variant="secondary"
            text={intl.get("Cancel").d("Cancel")}
            onClick={handleCloseUploadModal}
            disabled={uploading}
          />
          <DmsButton
            variant="primary"
            text={uploading ? intl.get("Uploading").d("Uploading...") : intl.get("Upload").d("Upload")}
            onClick={handleUpload}
            disabled={uploading || !uploadFile || !selectedDocType}
          />
        </DialogActions>
      </Dialog>

      {/* Document Viewer Modal */}
      <DmsFileViewerModal
        isOpen={viewerOpen}
        onClose={handleCloseViewer}
        filePk={selectedDocument?.filePk}
        fileName={selectedDocument?.fileName}
      />
    </>
  );
};

InstitutionDocuments.propTypes = {
  institutionPk: PropTypes.number,
  allowUpload: PropTypes.bool,
};

export default InstitutionDocuments;
